// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    indicators: {
      label: 'Indicadores de mercado',
      help: 'Una fila por lectura de un indicador externo. Agrupa siempre por indicador',
    },
    impacts: {
      label: 'Impacto en la casa',
      help: 'Una fila por impacto evaluado: lo que un movimiento de fuera vale aquí. Todo son estimaciones',
    },
    dates: {
      at: 'Fecha de la lectura',
      assessed: 'Fecha del cálculo',
    },
    metrics: {
      value: 'Valor medio',
      high: 'Máximo',
      low: 'Mínimo',
      readings: 'Lecturas',
      spread: 'Recorrido',
      costDelta: 'Impacto anual estimado',
      exposure: 'Gasto expuesto',
      impacts: 'Impactos evaluados',
      materials: 'Materiales afectados',
      suppliers: 'Proveedores afectados',
      change: 'Movimiento medio',
      confidence: 'Confianza',
    },
    dimensions: {
      indicator: 'Indicador',
      origin: 'Origen de la lectura',
      source: 'Fuente',
      impactIndicator: 'Indicador',
      direction: 'A favor o en contra',
      severity: 'Gravedad',
    },
    filters: {
      value: 'Valor de la lectura',
      change: 'Tamaño del movimiento',
      confidence: 'Confianza',
    },
  },
  ca: {
    indicators: {
      label: 'Indicadors de mercat',
      help: 'Una fila per lectura d\'un indicador extern. Agrupa sempre per indicador',
    },
    impacts: {
      label: 'Impacte a la casa',
      help: 'Una fila per impacte avaluat: el que un moviment de fora val aquí. Tot són estimacions',
    },
    dates: {
      at: 'Data de la lectura',
      assessed: 'Data del càlcul',
    },
    metrics: {
      value: 'Valor mitjà',
      high: 'Màxim',
      low: 'Mínim',
      readings: 'Lectures',
      spread: 'Recorregut',
      costDelta: 'Impacte anual estimat',
      exposure: 'Despesa exposada',
      impacts: 'Impactes avaluats',
      materials: 'Materials afectats',
      suppliers: 'Proveïdors afectats',
      change: 'Moviment mitjà',
      confidence: 'Confiança',
    },
    dimensions: {
      indicator: 'Indicador',
      origin: 'Origen de la lectura',
      source: 'Font',
      impactIndicator: 'Indicador',
      direction: 'A favor o en contra',
      severity: 'Gravetat',
    },
    filters: {
      value: 'Valor de la lectura',
      change: 'Mida del moviment',
      confidence: 'Confiança',
    },
  },
  en: {
    indicators: {
      label: 'Market indicators',
      help: 'One row per reading of an outside indicator. Always group by indicator',
    },
    impacts: {
      label: 'Impact on the company',
      help: 'One row per assessed impact: what an outside move is worth here. Everything is an estimate',
    },
    dates: {
      at: 'Reading date',
      assessed: 'Assessed on',
    },
    metrics: {
      value: 'Average value',
      high: 'High',
      low: 'Low',
      readings: 'Readings',
      spread: 'Spread',
      costDelta: 'Estimated annual impact',
      exposure: 'Spend exposed',
      impacts: 'Impacts assessed',
      materials: 'Materials affected',
      suppliers: 'Suppliers affected',
      change: 'Average move',
      confidence: 'Confidence',
    },
    dimensions: {
      indicator: 'Indicator',
      origin: 'Reading origin',
      source: 'Source',
      impactIndicator: 'Indicator',
      direction: 'For or against',
      severity: 'Severity',
    },
    filters: {
      value: 'Reading value',
      change: 'Size of the move',
      confidence: 'Confidence',
    },
  },
  fr: {
    indicators: {
      label: 'Indicateurs de marché',
      help: 'Une ligne par relevé d\'un indicateur externe. Regroupez toujours par indicateur',
    },
    impacts: {
      label: 'Impact sur l\'entreprise',
      help: 'Une ligne par impact évalué : ce qu\'un mouvement extérieur vaut ici. Tout est une estimation',
    },
    dates: {
      at: 'Date du relevé',
      assessed: 'Date de l\'évaluation',
    },
    metrics: {
      value: 'Valeur moyenne',
      high: 'Maximum',
      low: 'Minimum',
      readings: 'Relevés',
      spread: 'Amplitude',
      costDelta: 'Impact annuel estimé',
      exposure: 'Dépense exposée',
      impacts: 'Impacts évalués',
      materials: 'Matériaux touchés',
      suppliers: 'Fournisseurs touchés',
      change: 'Variation moyenne',
      confidence: 'Confiance',
    },
    dimensions: {
      indicator: 'Indicateur',
      origin: 'Origine du relevé',
      source: 'Source',
      impactIndicator: 'Indicateur',
      direction: 'Favorable ou non',
      severity: 'Gravité',
    },
    filters: {
      value: 'Valeur du relevé',
      change: 'Ampleur du mouvement',
      confidence: 'Confiance',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
